// @ts-nocheck
/**
 * EPG API Client
 *
 * Service layer for Event Planning Grid API endpoints.
 * Handles all HTTP communication with the EPG backend.
 *
 * Created: 2025-12-14
 * Updated: 2025-12-15 - Aligned with backend Month Code enum (JAN, FEB, MAR)
 * Phase: 2 - Frontend Build
 *
 * Backend Endpoints (from backend/routers/epg.py):
 * - GET /api/epg/organizations - List 15DP opportunity organizations
 * - GET /api/epg/organizations/:id - Get organization detail with contacts
 * - GET /api/epg/organizations/:id/events - Get organization events (12 months)
 * - GET /api/epg/organizations/:id/contacts - Get organization contacts
 * - GET /api/epg/event-options/:category - Get event options for category
 * - PUT /api/epg/organizations/:id/events - Update events for a month
 * - POST /api/epg/batch-update - Batch update multiple organizations
 * - GET /api/epg/sync/status - Get sync status
 * - POST /api/epg/sync/commit - Commit pending changes to Pipedrive
 * - DELETE /api/epg/sync/discard - Discard all pending changes
 */

import { apiClient, handleApiError } from './apiClient';
import { logger } from '@shared/utils/logger';
import type {
  MonthCode,
  OrganizationsListResponse,
  OrganizationDetail,
  EventsResponse,
  ContactInfo,
  EPGContact,
  EventOptionsResponse,
  UpdateEventsRequest,
  UpdateEventsResponse,
  BatchUpdateRequest,
  BatchUpdateResponse,
  SyncStatusResponse,
  SyncCommitRequest,
  SyncCommitResponse,
  OrganizationListParams,
  FieldDefinitionsResponse,
  SyncFieldDefinitionsResponse,
  PendingChange,
  EventCompletion,
} from '@shared/types/epg';

/**
 * Pipedrive API Response Types (Internal)
 *
 * These types represent the raw response structure from Pipedrive API.
 * Used internally by EPGApiClient to transform to EPGContact interface.
 */

/**
 * Pipedrive email object
 */
interface PipedriveEmail {
  label: string;
  value: string;
  primary?: boolean;
}

/**
 * Pipedrive organization reference
 */
interface PipedriveOrgReference {
  value: number;
  name: string;
}

/**
 * Pipedrive contact/person response
 *
 * This is a subset of the full Pipedrive Person object.
 * Only includes fields used by EPG Contact mapping.
 */
interface PipedriveContact {
  id: number;
  name: string;
  email?: PipedriveEmail[];
  org_id?: number | PipedriveOrgReference;
  org_name?: string;
  organization?: string; // Flattened org name from backend /epg/contacts endpoint
  [key: string]: unknown; // Custom fields (e.g., job_title, work_location)
}

/**
 * EPG API Client
 */
export class EPGApiClient {
  /**
   * ============================================
   * Organization Endpoints
   * ============================================
   */

  /**
   * Get paginated list of 15DP opportunity organizations
   *
   * @param params Query parameters for filtering/pagination
   * @returns Promise resolving to organizations list response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.getOrganizations({ page: 1, limit: 50 });
   * console.log(`Found ${result.total} organizations`);
   * ```
   */
  async getOrganizations(params: OrganizationListParams = {}): Promise<OrganizationsListResponse> {
    try {
      const { page = 1, limit = 50, search = null } = params;

      logger.log(`[EPG API] Fetching organizations (page=${page}, limit=${limit}, search=${search})`);

      const response = await apiClient.get<OrganizationsListResponse>('/epg/organizations', {
        params: {
          page,
          limit,
          search,
        },
      });

      logger.log(
        `[EPG API] Fetched ${response.data.organizations.length} organizations (${response.data.total} total)`
      );

      // Debug: Check if organizations have Pipedrive field hashes
      if (response.data.organizations.length > 0) {
        const firstOrg = response.data.organizations[0];
        const fieldHashCount = Object.keys(firstOrg).filter(k => k.length === 40).length;
        logger.log(`[EPG API] First organization has ${fieldHashCount} Pipedrive field hashes (should be 36 for all 3 categories x 12 months)`);
      }

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch organizations:', message);
      throw new Error(message);
    }
  }

  /**
   * Get organization detail with contacts and marketing data
   *
   * @param orgId Organization ID
   * @returns Promise resolving to organization detail
   *
   * @throws Error if API request fails or organization not found
   *
   * @example
   * ```ts
   * const org = await epgApi.getOrganization(12345);
   * console.log(`Loaded ${org.name} with ${org.contacts.length} contacts`);
   * ```
   */
  async getOrganization(orgId: number): Promise<OrganizationDetail> {
    try {
      logger.log(`[EPG API] Fetching organization detail for org ${orgId}`);

      const response = await apiClient.get<OrganizationDetail>(`/epg/organizations/${orgId}`);

      logger.log(`[EPG API] Fetched organization: ${response.data.name}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to fetch organization ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * Get organization events for 12 months
   *
   * @param orgId Organization ID
   * @returns Promise resolving to organization events response
   *
   * @throws Error if API request fails or organization not found
   *
   * @example
   * ```ts
   * const result = await epgApi.getOrganizationEvents(12345);
   * console.log(`Loaded events for org ${result.org_id}`);
   * ```
   */
  async getOrganizationEvents(orgId: number): Promise<EventsResponse> {
    try {
      logger.log(`[EPG API] Fetching events for org ${orgId}`);

      const response = await apiClient.get<EventsResponse>(`/epg/organizations/${orgId}/events`);

      logger.log(`[EPG API] Fetched events for org ${orgId}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to fetch events for org ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * Get organization contacts
   *
   * @param orgId Organization ID
   * @returns Promise resolving to array of contacts
   *
   * @throws Error if API request fails or organization not found
   *
   * @example
   * ```ts
   * const contacts = await epgApi.getOrganizationContacts(12345);
   * console.log(`Found ${contacts.length} contacts`);
   * ```
   */
  async getOrganizationContacts(orgId: number): Promise<ContactInfo[]> {
    try {
      logger.log(`[EPG API] Fetching contacts for org ${orgId}`);

      const response = await apiClient.get<ContactInfo[]>(`/epg/organizations/${orgId}/contacts`);

      logger.log(`[EPG API] Fetched ${response.data.length} contacts for org ${orgId}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to fetch contacts for org ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Contact Endpoints (27 Fields from Pipedrive)
   * ============================================
   */

  /**
   * Get all EPG contacts across all organizations
   *
   * Fetches all contacts from Pipedrive with full 27 field mapping.
   * Implements company grouping logic for color banding in UI.
   *
   * @returns Promise resolving to array of EPG contacts
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const contacts = await epgApi.fetchEPGContacts();
   * console.log(`Loaded ${contacts.length} contacts`);
   * ```
   */
  async fetchEPGContacts(): Promise<EPGContact[]> {
    try {
      logger.log('[EPG API] Fetching all EPG contacts');

      // Call backend endpoint (Pipedrive persons/search API)
      const response = await apiClient.get<{ success: boolean; contacts: PipedriveContact[] }>('/epg/contacts');

      if (!response.data.success) {
        throw new Error('Failed to fetch contacts from backend');
      }

      const rawContacts = response.data.contacts;

      // Transform Pipedrive API response to EPGContact interface
      const contacts: EPGContact[] = rawContacts.map((contact) => ({
        // Core fields (always present)
        person_id: contact.id || 0,
        name: contact.name || '',
        organization: this.extractOrganizationName(contact),
        organization_id: this.extractOrganizationId(contact),

        // Contact info fields - backend returns 'title' directly (flattened)
        title: this.extractFieldValue(contact, 'title') || this.extractFieldValue(contact, 'job_title'),
        email_work: this.extractEmailValue(contact, 'work'),
        email_home: this.extractEmailValue(contact, 'home'),
        email_other: this.extractEmailValue(contact, 'other'),
        work_location: this.extractFieldValue(contact, 'work_location') || 'None Provided',

        // Activity fields
        last_activity_date: this.extractFieldValue(contact, 'last_activity_date'),
        activity_trigger: this.extractFieldValue(contact, 'activity_trigger'),
        next_activity_description: this.extractFieldValue(contact, 'next_activity_description'),
        total_activities: this.parseIntField(contact, 'activities_count'),
        activity_overview: this.extractFieldValue(contact, 'activity_overview'),
        activities_to_do: this.parseIntField(contact, 'open_deals_count'),

        // Email & messaging fields
        email_messages_count: this.parseIntField(contact, 'email_messages_count'),
        last_invitation_sent: this.extractFieldValue(contact, 'last_invitation_sent'),
        last_invite_details: this.extractFieldValue(contact, 'last_invite_details'),

        // Metadata fields
        person_created: this.extractFieldValue(contact, 'add_time'),
        organization_contact_priority: this.parseIntField(contact, 'organization_contact_priority'),

        // Event types (7 checkboxes from desktop EPG)
        event_types: this.extractEventTypes(contact),

        // Company grouping data (for color banding in UI)
        company_color_index: 0, // Will be calculated client-side based on organization
      }));

      // Group by organization for color banding
      const groupedContacts = this.groupContactsByOrganization(contacts);

      logger.log(`[EPG API] Fetched ${contacts.length} contacts across ${groupedContacts.size} organizations`);

      return contacts;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch EPG contacts:', message);
      throw new Error(message);
    }
  }

  /**
   * Get contacts filtered by organization event selections
   *
   * Fetches contacts ONLY from organizations that have the specified event categories
   * selected in the given months. Used by HMC email wizard for Select Recipients filtering.
   *
   * @param months Array of month codes (JAN, FEB, etc.)
   * @param eventCategories Array of event category names ("Sporting", "Concerts")
   * @param category EPG category name (default: "Events")
   * @returns Promise resolving to filtered contacts and organization count
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * // Get contacts from orgs with "Concerts" events in JAN
   * const result = await epgApi.fetchFilteredContacts(['JAN'], ['Concerts']);
   * console.log(`Found ${result.contacts.length} contacts from ${result.filteredOrgCount} orgs`);
   * ```
   */
  async fetchFilteredContacts(
    months: string[],
    eventCategories: string[],
    category: string = 'Events',
    eventOptionIds?: number[]
  ): Promise<{
    contacts: EPGContact[];
    total: number;
    filteredOrgCount: number;
  }> {
    try {
      logger.log(
        `[EPG API] Fetching filtered contacts: months=${months.join(',')}, eventCategories=${eventCategories.join(',')}, category=${category}, eventOptionIds=${eventOptionIds?.join(',') || 'none'}`
      );

      // Build query params - FastAPI expects repeated params for lists
      const params = new URLSearchParams();
      months.forEach(m => params.append('months', m));

      // If specific event option IDs are provided, use them instead of categories
      if (eventOptionIds && eventOptionIds.length > 0) {
        eventOptionIds.forEach(id => params.append('event_option_ids', id.toString()));
      } else {
        eventCategories.forEach(cat => params.append('event_categories', cat));
      }
      params.append('category', category);

      const response = await apiClient.get<{
        success: boolean;
        contacts: PipedriveContact[];
        total: number;
        filtered_org_count: number;
      }>(`/epg/contacts/filtered?${params.toString()}`);

      if (!response.data.success) {
        throw new Error('Failed to fetch filtered contacts from backend');
      }

      const rawContacts = response.data.contacts;

      // Transform to EPGContact interface (same mapping as fetchEPGContacts)
      const contacts: EPGContact[] = rawContacts.map((contact) => ({
        person_id: contact.id || 0,
        name: contact.name || '',
        organization: this.extractOrganizationName(contact),
        organization_id: this.extractOrganizationId(contact),
        title: this.extractFieldValue(contact, 'title') || this.extractFieldValue(contact, 'job_title'),
        email_work: this.extractEmailValue(contact, 'work'),
        email_home: this.extractEmailValue(contact, 'home'),
        email_other: this.extractEmailValue(contact, 'other'),
        work_location: this.extractFieldValue(contact, 'work_location') || 'None Provided',
        last_activity_date: this.extractFieldValue(contact, 'last_activity_date'),
        activity_trigger: this.extractFieldValue(contact, 'activity_trigger'),
        next_activity_description: this.extractFieldValue(contact, 'next_activity_description'),
        total_activities: this.parseIntField(contact, 'activities_count'),
        activity_overview: this.extractFieldValue(contact, 'activity_overview'),
        activities_to_do: this.parseIntField(contact, 'open_deals_count'),
        email_messages_count: this.parseIntField(contact, 'email_messages_count'),
        last_invitation_sent: this.extractFieldValue(contact, 'last_invitation_sent'),
        last_invite_details: this.extractFieldValue(contact, 'last_invite_details'),
        person_created: this.extractFieldValue(contact, 'add_time'),
        organization_contact_priority: this.parseIntField(contact, 'organization_contact_priority'),
        event_types: this.extractEventTypes(contact),
        company_color_index: 0,
      }));

      // Group by organization for color banding
      this.groupContactsByOrganization(contacts);

      logger.log(
        `[EPG API] Fetched ${contacts.length} filtered contacts from ${response.data.filtered_org_count} organizations`
      );

      return {
        contacts,
        total: response.data.total,
        filteredOrgCount: response.data.filtered_org_count,
      };
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch filtered contacts:', message);
      throw new Error(message);
    }
  }

  /**
   * Get contacts for a specific organization
   *
   * Fetches contacts for a single organization with full 27 field mapping.
   *
   * @param orgId Organization ID
   * @returns Promise resolving to array of EPG contacts
   *
   * @throws Error if API request fails or organization not found
   *
   * @example
   * ```ts
   * const contacts = await epgApi.fetchContactsByOrganization(12345);
   * console.log(`Found ${contacts.length} contacts for org 12345`);
   * ```
   */
  async fetchContactsByOrganization(orgId: number): Promise<EPGContact[]> {
    try {
      logger.log(`[EPG API] Fetching contacts for organization ${orgId}`);

      // Call backend endpoint (Pipedrive persons by org_id)
      // Backend returns array directly, not wrapped in { success, contacts }
      const response = await apiClient.get<PipedriveContact[]>(
        `/epg/organizations/${orgId}/contacts`
      );

      const rawContacts = response.data;

      // Transform to EPGContact interface (same mapping as fetchEPGContacts)
      const contacts: EPGContact[] = rawContacts.map((contact, index) => ({
        person_id: contact.id || 0,
        name: contact.name || '',
        organization: this.extractOrganizationName(contact),
        organization_id: this.extractOrganizationId(contact),
        title: this.extractFieldValue(contact, 'title') || this.extractFieldValue(contact, 'job_title'),
        email_work: this.extractEmailValue(contact, 'work'),
        email_home: this.extractEmailValue(contact, 'home'),
        email_other: this.extractEmailValue(contact, 'other'),
        work_location: this.extractFieldValue(contact, 'work_location') || 'None Provided',
        last_activity_date: this.extractFieldValue(contact, 'last_activity_date'),
        activity_trigger: this.extractFieldValue(contact, 'activity_trigger'),
        next_activity_description: this.extractFieldValue(contact, 'next_activity_description'),
        total_activities: this.parseIntField(contact, 'activities_count'),
        activity_overview: this.extractFieldValue(contact, 'activity_overview'),
        activities_to_do: this.parseIntField(contact, 'open_deals_count'),
        email_messages_count: this.parseIntField(contact, 'email_messages_count'),
        last_invitation_sent: this.extractFieldValue(contact, 'last_invitation_sent'),
        last_invite_details: this.extractFieldValue(contact, 'last_invite_details'),
        person_created: this.extractFieldValue(contact, 'add_time'),
        organization_contact_priority: this.parseIntField(contact, 'organization_contact_priority'),
        event_types: this.extractEventTypes(contact),
        company_color_index: index % 2, // Alternate colors within same org
      }));

      logger.log(`[EPG API] Fetched ${contacts.length} contacts for org ${orgId}`);

      return contacts;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to fetch contacts for org ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Contact Helper Methods (Private)
   * ============================================
   */

  /**
   * Extract organization name from Pipedrive contact response
   */
  private extractOrganizationName(contact: PipedriveContact): string {
    // Check for flattened 'organization' field (from backend /epg/contacts endpoint)
    if (contact.organization && typeof contact.organization === 'string') {
      return contact.organization;
    }
    if (contact.org_id && typeof contact.org_id === 'object') {
      return contact.org_id.name || '';
    }
    if (contact.org_name) {
      return contact.org_name;
    }
    return '';
  }

  /**
   * Extract organization ID from Pipedrive contact response
   */
  private extractOrganizationId(contact: PipedriveContact): number | null {
    if (contact.org_id && typeof contact.org_id === 'object') {
      return contact.org_id.value || null;
    }
    if (contact.org_id && typeof contact.org_id === 'number') {
      return contact.org_id;
    }
    return null;
  }

  /**
   * Extract email value by type (work, home, other)
   *
   * Handles two data formats:
   * 1. Pipedrive raw: contact.email = [{ label: 'work', value: 'email@example.com' }]
   * 2. Backend flattened: contact.email_work = 'email@example.com'
   */
  private extractEmailValue(contact: PipedriveContact, emailType: string): string | null {
    // First, check for flattened format (backend already transformed)
    const flatFieldKey = `email_${emailType}`;
    if (contact[flatFieldKey] && typeof contact[flatFieldKey] === 'string') {
      return contact[flatFieldKey] as string;
    }

    // Second, check for Pipedrive array format
    if (contact.email && Array.isArray(contact.email)) {
      const email = contact.email.find((e) => e.label === emailType);
      return email?.value || null;
    }

    return null;
  }

  /**
   * Extract custom field value from Pipedrive contact
   */
  private extractFieldValue(contact: PipedriveContact, fieldKey: string): string | null {
    // Check direct field access
    if (contact[fieldKey] !== undefined && contact[fieldKey] !== null) {
      return String(contact[fieldKey]);
    }

    // Check custom fields object (if present)
    const customFields = contact.custom_fields as Record<string, unknown> | undefined;
    if (customFields && customFields[fieldKey] !== undefined) {
      return String(customFields[fieldKey]);
    }

    return null;
  }

  /**
   * Parse integer field from Pipedrive contact
   */
  private parseIntField(contact: PipedriveContact, fieldKey: string): number | null {
    const value = this.extractFieldValue(contact, fieldKey);
    if (value === null) {
      return null;
    }

    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Extract event types from Pipedrive contact (7 checkboxes)
   *
   * Desktop EPG has 7 event type checkboxes:
   * - Education, Sporting, Concerts, Tastings & HH, Outdoors, USI EB Events, Webinar
   */
  private extractEventTypes(contact: PipedriveContact): string {
    // First, check if backend already provided a joined string (flattened format)
    if (contact.event_types && typeof contact.event_types === 'string') {
      return contact.event_types;
    }

    // Otherwise, build from individual checkbox fields (Pipedrive raw format)
    const eventTypes: string[] = [];

    // Check each event type field (Pipedrive custom fields)
    const eventTypeFields = [
      { key: 'event_type_education', label: 'Education' },
      { key: 'event_type_sporting', label: 'Sporting' },
      { key: 'event_type_concerts', label: 'Concerts' },
      { key: 'event_type_tastings_hh', label: 'Tastings & HH' },
      { key: 'event_type_outdoors', label: 'Outdoors' },
      { key: 'event_type_usi', label: 'USI EB Events' },
      { key: 'event_type_webinar', label: 'Webinar' },
    ];

    for (const eventType of eventTypeFields) {
      const value = this.extractFieldValue(contact, eventType.key);
      // Pipedrive checkboxes are stored as 1/0 or true/false
      if (value === '1' || value === 'true' || value === true) {
        eventTypes.push(eventType.label);
      }
    }

    return eventTypes.join(', ');
  }

  /**
   * Group contacts by organization for color banding
   *
   * Returns Map of org_id -> contacts for UI rendering
   */
  private groupContactsByOrganization(contacts: EPGContact[]): Map<number, EPGContact[]> {
    const grouped = new Map<number, EPGContact[]>();

    for (const contact of contacts) {
      const orgId = contact.organization_id || 0;

      if (!grouped.has(orgId)) {
        grouped.set(orgId, []);
      }

      grouped.get(orgId)!.push(contact);
    }

    // Sort contacts within each organization by priority
    for (const [orgId, orgContacts] of grouped.entries()) {
      orgContacts.sort((a, b) => {
        const aPriority = a.organization_contact_priority || 999;
        const bPriority = b.organization_contact_priority || 999;
        return aPriority - bPriority;
      });

      // Assign alternating color index within organization
      orgContacts.forEach((contact, index) => {
        contact.company_color_index = index % 2;
      });
    }

    return grouped;
  }

  /**
   * ============================================
   * Event Option Endpoints
   * ============================================
   */

  /**
   * Get event options for a specific category
   *
   * @param category Category name ("Events", "Calls & Emails", "Drops")
   * @returns Promise resolving to event options response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.getEventOptions('Events');
   * console.log(`Found options for ${result.category}`);
   * ```
   */
  async getEventOptions(category: 'Events' | 'Calls & Emails' | 'Drops'): Promise<EventOptionsResponse> {
    try {
      logger.log(`[EPG API] Fetching event options for category: ${category}`);

      const response = await apiClient.get<EventOptionsResponse>(`/epg/events/available`, {
        params: { category }
      });

      logger.log(`[EPG API] Fetched event options for ${category}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to fetch event options for ${category}:`, message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Update Endpoints
   * ============================================
   */

  /**
   * Update event assignments for a single month
   *
   * @param orgId Organization ID
   * @param request Update request (month + event_ids)
   * @returns Promise resolving to update response
   *
   * @throws Error if API request fails or validation error
   *
   * @example
   * ```ts
   * const result = await epgApi.updateEvents(12345, {
   *   month: MonthCode.JAN,
   *   event_ids: [909, 911, 912],
   *   sync_to_pipedrive: true,
   * });
   * console.log(`Updated ${result.month} for org ${result.org_id}`);
   * ```
   */
  async updateEvents(orgId: number, request: UpdateEventsRequest): Promise<UpdateEventsResponse> {
    try {
      logger.log(`[EPG API] Updating events for org ${orgId}, month ${request.month}`);

      // Validate event_ids locally before sending
      if (request.event_ids.length > 15) {
        throw new Error('Maximum 15 event selections per month');
      }

      const response = await apiClient.put<UpdateEventsResponse>(
        `/epg/organizations/${orgId}/events`,
        request
      );

      logger.log(`[EPG API] Updated ${request.month} for org ${orgId}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to update events for org ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * Batch update event assignments for multiple organizations/months
   *
   * @param request Batch update request
   * @returns Promise resolving to batch update response
   *
   * @throws Error if API request fails or validation error
   *
   * @example
   * ```ts
   * const result = await epgApi.batchUpdate({
   *   updates: [
   *     { org_id: 12345, month: MonthCode.JAN, event_ids: [909, 911] },
   *     { org_id: 12346, month: MonthCode.JAN, event_ids: [923, 927] },
   *   ],
   *   sync_to_pipedrive: true,
   * });
   * console.log(`Updated ${result.successful} organizations`);
   * ```
   */
  async batchUpdate(request: BatchUpdateRequest): Promise<BatchUpdateResponse> {
    try {
      logger.log(`[EPG API] Batch updating ${request.updates.length} items`);

      // Validate event_ids for each update
      for (const update of request.updates) {
        if (update.event_ids.length > 15) {
          throw new Error(`Maximum 15 events per month (org: ${update.org_id}, month: ${update.month})`);
        }
      }

      const response = await apiClient.post<BatchUpdateResponse>('/epg/batch-update', request);

      logger.log(
        `[EPG API] Batch update completed: ${response.data.successful} succeeded, ${response.data.failed} failed`
      );

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to batch update:', message);
      throw new Error(message);
    }
  }

  /**
   * Batch update event assignments (alias for batchUpdate)
   *
   * This is an alias for batchUpdate() to match the naming used in pendingChangesStore.
   *
   * @param request Batch update request
   * @returns Promise resolving to batch update response
   *
   * @throws Error if API request fails or validation error
   *
   * @example
   * ```ts
   * const result = await epgApi.batchUpdateEvents({
   *   updates: [
   *     { org_id: 12345, month: MonthCode.JAN, event_ids: [909, 911] },
   *   ],
   * });
   * console.log(`Updated ${result.successful} organizations`);
   * ```
   */
  async batchUpdateEvents(request: BatchUpdateRequest): Promise<BatchUpdateResponse> {
    return this.batchUpdate(request);
  }

  /**
   * ============================================
   * Sync Endpoints
   * ============================================
   */

  /**
   * Get sync status and pending changes
   *
   * @returns Promise resolving to sync status response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const status = await epgApi.getSyncStatus();
   * console.log(`Pending changes: ${status.pending_org_changes}`);
   * ```
   */
  async getSyncStatus(): Promise<SyncStatusResponse> {
    try {
      logger.log('[EPG API] Fetching sync status');

      const response = await apiClient.get<SyncStatusResponse>('/epg/sync/status');

      logger.log(
        `[EPG API] Sync status: ${response.data.pending_org_changes} org changes, ${response.data.pending_contact_changes} contact changes`
      );

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch sync status:', message);
      throw new Error(message);
    }
  }

  /**
   * Commit pending changes to Pipedrive
   *
   * @param request Sync commit request (what to sync)
   * @returns Promise resolving to sync commit response
   *
   * @throws Error if API request fails or sync error
   *
   * @example
   * ```ts
   * const result = await epgApi.commitSync({
   *   include_orgs: true,
   *   include_contacts: true,
   * });
   * console.log(`Synced ${result.total_changes} changes`);
   * ```
   */
  async commitSync(request: SyncCommitRequest): Promise<SyncCommitResponse> {
    try {
      logger.log('[EPG API] Committing sync to Pipedrive');

      const response = await apiClient.post<SyncCommitResponse>('/epg/sync/commit', request);

      logger.log(
        `[EPG API] Sync completed: ${response.data.org_changes_synced} org changes, ${response.data.contact_changes_synced} contact changes`
      );

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to commit sync:', message);
      throw new Error(message);
    }
  }

  /**
   * Discard all pending changes
   *
   * @returns Promise resolving when changes are discarded
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * await epgApi.discardChanges();
   * console.log('Discarded all pending changes');
   * ```
   */
  async discardChanges(): Promise<void> {
    try {
      logger.log('[EPG API] Discarding all pending changes');

      await apiClient.delete('/epg/sync/discard');

      logger.log('[EPG API] Discarded all pending changes');
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to discard changes:', message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Push/Pull Operations (Phase 5)
   * ============================================
   */

  /**
   * Push pending changes to Pipedrive (Phase 5 - P5-T01)
   *
   * Batch update all pending changes and sync to Pipedrive.
   * Shows confirmation dialog if more than 10 changes.
   * Displays progress indicator during sync.
   *
   * @param request Batch update request
   * @returns Promise resolving to batch update response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.pushToPipedrive({
   *   updates: unsavedChanges.updates,
   *   sync_to_pipedrive: true,
   * });
   * console.log(`Pushed ${result.successful} changes to Pipedrive`);
   * ```
   */
  async pushToPipedrive(request: BatchUpdateRequest): Promise<BatchUpdateResponse> {
    try {
      logger.log(`[EPG API] Pushing ${request.updates.length} changes to Pipedrive`);

      // Use batchUpdate with sync_to_pipedrive flag
      const response = await this.batchUpdate({
        ...request,
        sync_to_pipedrive: true,
      });

      logger.log(
        `[EPG API] Push completed: ${response.successful} succeeded, ${response.failed} failed`
      );

      return response;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to push to Pipedrive:', message);
      throw new Error(message);
    }
  }

  /**
   * Pull fresh data from Pipedrive (Phase 5 - P5-T02)
   *
   * Refreshes organization events from Pipedrive API.
   * Warns if there are unsaved changes before pulling.
   *
   * @param orgId Organization ID to refresh
   * @returns Promise resolving to fresh events data
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const events = await epgApi.pullFromPipedrive(12345);
   * console.log(`Pulled fresh data for org ${events.org_id}`);
   * ```
   */
  async pullFromPipedrive(orgId: number): Promise<EventsResponse> {
    try {
      logger.log(`[EPG API] Pulling fresh data from Pipedrive for org ${orgId}`);

      // Force refresh from Pipedrive by adding force_refresh query param
      const response = await apiClient.get<EventsResponse>(
        `/epg/organizations/${orgId}/events`,
        {
          params: { force_refresh: true }
        }
      );

      logger.log(`[EPG API] Pulled fresh data for org ${orgId}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to pull from Pipedrive for org ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Pending Changes Endpoints (Phase 6)
   * ============================================
   */

  /**
   * Get all pending changes from backend
   *
   * @returns Promise resolving to array of pending changes
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const changes = await epgApi.getPendingChanges();
   * console.log(`Loaded ${changes.length} pending changes`);
   * ```
   */
  async getPendingChanges(): Promise<PendingChange[]> {
    try {
      logger.log('[EPG API] Fetching pending changes');

      // Backend returns snake_case - we need to transform to camelCase
      interface BackendPendingChange {
        org_id: number;
        org_name: string;
        category: string;
        month: string;
        old_event_ids: number[];
        new_event_ids: number[];
      }

      // Backend returns { success, count, changes } - extract the changes array
      const response = await apiClient.get<{ success: boolean; count: number; changes: BackendPendingChange[] }>('/epg/pending-changes');

      const rawChanges = response.data?.changes || [];

      // Transform snake_case to camelCase
      const changes: PendingChange[] = rawChanges.map((change) => ({
        orgId: change.org_id,
        orgName: change.org_name,
        category: change.category,
        month: change.month as MonthCode,
        oldIds: change.old_event_ids || [],
        newIds: change.new_event_ids || [],
      }));

      logger.log(`[EPG API] Fetched ${changes.length} pending changes`);

      return changes;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch pending changes:', message);
      throw new Error(message);
    }
  }

  /**
   * Save a pending change to backend
   *
   * @param change Pending change to save
   * @returns Promise resolving when change is saved
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * await epgApi.savePendingChange({
   *   orgId: 12345,
   *   orgName: 'ABC Corporation',
   *   category: 'Events',
   *   month: MonthCode.JAN,
   *   oldIds: [909, 911],
   *   newIds: [909, 911, 912],
   * });
   * ```
   */
  async savePendingChange(change: PendingChange): Promise<void> {
    try {
      logger.log(`[EPG API] Saving pending change for org ${change.orgId}, ${change.category}, ${change.month}`);

      // Transform camelCase to snake_case for backend API
      const backendPayload = {
        org_id: change.orgId,
        org_name: change.orgName,
        category: change.category,
        month: change.month,
        old_event_ids: change.oldIds,
        new_event_ids: change.newIds,
      };

      await apiClient.post('/epg/pending-changes', backendPayload);

      logger.log('[EPG API] Saved pending change');
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to save pending change:', message);
      throw new Error(message);
    }
  }

  /**
   * Delete a specific pending change from backend
   *
   * @param orgId Organization ID
   * @param category Category name
   * @param month Month code
   * @returns Promise resolving when change is deleted
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * await epgApi.deletePendingChange(12345, 'Events', MonthCode.JAN);
   * ```
   */
  async deletePendingChange(orgId: number, category: string, month: string): Promise<void> {
    try {
      const cellKey = `${orgId}-${category}-${month}`;
      logger.log(`[EPG API] Deleting pending change: ${cellKey}`);

      await apiClient.delete(`/epg/pending-changes/${cellKey}`);

      logger.log('[EPG API] Deleted pending change');
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to delete pending change:', message);
      throw new Error(message);
    }
  }

  /**
   * Delete all pending changes from backend
   *
   * @returns Promise resolving when all changes are deleted
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * await epgApi.deleteAllPendingChanges();
   * console.log('Deleted all pending changes');
   * ```
   */
  async deleteAllPendingChanges(): Promise<void> {
    try {
      logger.log('[EPG API] Deleting all pending changes');

      await apiClient.delete('/epg/pending-changes');

      logger.log('[EPG API] Deleted all pending changes');
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to delete all pending changes:', message);
      throw new Error(message);
    }
  }

  /**
   * Clear pending changes for deleted organizations
   *
   * Checks each organization with pending changes against Pipedrive API
   * and removes pending changes for organizations that have been deleted.
   *
   * @returns Promise resolving to response with cleared organizations
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.clearDeletedOrgChanges();
   * console.log(`Cleared pending changes for ${result.cleared_orgs.length} deleted organizations`);
   * ```
   */
  async clearDeletedOrgChanges(): Promise<{ success: boolean; cleared_orgs: Array<{ org_id: number; org_name: string }> }> {
    try {
      logger.log('[EPG API] Clearing pending changes for deleted organizations');

      const response = await apiClient.delete<{ success: boolean; cleared_orgs: Array<{ org_id: number; org_name: string }> }>(
        '/epg/pending-changes/deleted-orgs'
      );

      logger.log(`[EPG API] Cleared pending changes for ${response.data.cleared_orgs.length} deleted organizations`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to clear deleted org changes:', message);
      throw new Error(message);
    }
  }

  /**
   * Push contact event type changes to Pipedrive
   *
   * Updates contact persons with their new event type preferences.
   *
   * @param changes - Array of contact changes with contact_id and event_types
   * @returns Promise resolving to response with success status and results
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.pushContactChanges([
   *   { contact_id: 123, event_types: 'Education, Sporting' },
   *   { contact_id: 456, event_types: 'Webinar' }
   * ]);
   * console.log(`Updated ${result.results.length} contacts`);
   * ```
   */
  async pushContactChanges(
    changes: Array<{ contact_id: number; event_types: string }>
  ): Promise<{ success: boolean; results: Array<{ contact_id: number; success: boolean }>; error?: string }> {
    try {
      logger.log(`[EPG API] Pushing ${changes.length} contact changes to Pipedrive`);

      // Use longer timeout for batch operations (60s = ~50ms/contact * 1000 contacts + buffer)
      const response = await apiClient.post<{
        success: boolean;
        results: Array<{ contact_id: number; success: boolean }>;
        error?: string;
      }>('/epg/contacts/push', { changes }, {
        timeout: 60000, // 60 seconds for batch Pipedrive updates
      });

      logger.log(`[EPG API] Pushed ${changes.length} contact changes to Pipedrive`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to push contact changes:', message);
      return { success: false, results: [], error: message };
    }
  }

  /**
   * Push contact changes with real-time progress streaming via SSE
   *
   * Uses Server-Sent Events to stream progress updates as each contact is processed.
   * This enables the UI to show "X/Y changes successfully pushed" in real-time.
   *
   * @param changes - Array of contact changes with contact_id and event_types
   * @param onProgress - Callback function invoked with progress updates
   * @returns Promise resolving to final response with success status and results
   *
   * @example
   * ```ts
   * const result = await epgApi.pushContactChangesWithProgress(
   *   [{ contact_id: 123, event_types: 'Education' }],
   *   (progress) => {
   *     console.log(`Progress: ${progress.current}/${progress.total}`);
   *     setProgressState(progress);
   *   }
   * );
   * ```
   */
  async pushContactChangesWithProgress(
    changes: Array<{ contact_id: number; event_types: string }>,
    onProgress: (progress: {
      current: number;
      total: number;
      successful: number;
      failed: number;
      status: 'pushing' | 'completed' | 'partial' | 'error';
      message: string;
    }) => void
  ): Promise<{
    success: boolean;
    successful: number;
    failed: number;
    results: Array<{ contact_id: number; success: boolean; error?: string | null }>;
    error?: string;
  }> {
    try {
      logger.log(`[EPG API] Starting SSE push for ${changes.length} contact changes`);

      // Get the base URL from apiClient
      const baseURL = apiClient.defaults.baseURL || '';

      // Create the SSE connection using fetch with POST
      const response = await fetch(`${baseURL}/epg/contacts/push-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({ changes }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult: {
        success: boolean;
        successful: number;
        failed: number;
        results: Array<{ contact_id: number; success: boolean; error?: string | null }>;
        error?: string;
      } = {
        success: false,
        successful: 0,
        failed: changes.length,
        results: [],
        error: 'Stream ended unexpectedly',
      };

      // Read the stream
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          logger.log('[EPG API] SSE stream closed');
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events (separated by double newline)
        const events = buffer.split('\n\n');
        buffer = events.pop() || ''; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Parse SSE event (format: "data: {...}")
          const match = event.match(/^data:\s*(.+)$/m);
          if (match) {
            try {
              const data = JSON.parse(match[1]);
              logger.log(`[EPG API] SSE progress: ${data.current}/${data.total} (${data.status})`);

              // Call progress callback
              onProgress({
                current: data.current,
                total: data.total,
                successful: data.successful,
                failed: data.failed,
                status: data.status,
                message: data.message,
              });

              // If this is the final event, capture the result
              if (data.status === 'completed' || data.status === 'partial' || data.status === 'error') {
                finalResult = {
                  success: data.failed === 0,
                  successful: data.successful,
                  failed: data.failed,
                  results: data.results || [],
                  error: data.failed > 0 ? `${data.failed} contact(s) failed to update` : undefined,
                };
              }
            } catch (parseError) {
              logger.warn('[EPG API] Failed to parse SSE event:', parseError);
            }
          }
        }
      }

      logger.log(`[EPG API] SSE push completed: ${finalResult.successful} succeeded, ${finalResult.failed} failed`);
      return finalResult;

    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] SSE push failed:', message);

      // Call progress callback with error state
      onProgress({
        current: 0,
        total: changes.length,
        successful: 0,
        failed: changes.length,
        status: 'error',
        message: message,
      });

      return {
        success: false,
        successful: 0,
        failed: changes.length,
        results: [],
        error: message,
      };
    }
  }

  /**
   * ============================================
   * Email Verification Endpoints
   * ============================================
   */

  /**
   * Verify if an email address is valid and deliverable
   *
   * Uses Rapid Email Verifier API (https://rapid-email-verifier.fly.dev/)
   * Results are cached in PostgreSQL for 30 days.
   *
   * @param email Email address to verify
   * @returns Promise resolving to verification result
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.verifyEmail('test@example.com');
   * if (result.valid) {
   *   console.log('Email is valid!');
   * } else {
   *   console.log(`Invalid: ${result.reason}`);
   * }
   * ```
   */
  async verifyEmail(email: string): Promise<{
    email: string;
    valid: boolean;
    reason?: string;
    cached?: boolean;
  }> {
    try {
      logger.log(`[EPG API] Verifying email: ${email}`);

      const response = await apiClient.get<{
        email: string;
        valid: boolean;
        reason?: string;
        cached?: boolean;
      }>('/epg/email/verify', {
        params: { email }
      });

      logger.log(`[EPG API] Email verification for ${email}: valid=${response.data.valid}, cached=${response.data.cached}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to verify email ${email}:`, message);
      // Return error state instead of throwing
      return {
        email,
        valid: false,
        reason: message,
        cached: false,
      };
    }
  }

  /**
   * Push email changes to Pipedrive contacts
   *
   * Updates contact persons with new email addresses.
   *
   * @param changes Array of email changes
   * @returns Promise resolving to push response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.pushEmailChanges([
   *   { contact_id: 123, email: 'new@example.com' },
   * ]);
   * console.log(`Updated ${result.successful} contacts`);
   * ```
   */
  async pushEmailChanges(
    changes: Array<{ contact_id: number; email: string }>
  ): Promise<{
    success: boolean;
    successful: number;
    failed: number;
    results: Array<{ contact_id: number; success: boolean; error?: string }>;
    error?: string;
  }> {
    try {
      logger.log(`[EPG API] Pushing ${changes.length} email changes to Pipedrive`);

      const response = await apiClient.post<{
        success: boolean;
        successful: number;
        failed: number;
        results: Array<{ contact_id: number; success: boolean; error?: string }>;
        error?: string;
      }>('/epg/contacts/push-emails', { changes });

      logger.log(`[EPG API] Pushed ${changes.length} email changes: ${response.data.successful} succeeded, ${response.data.failed} failed`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to push email changes:', message);
      return {
        success: false,
        successful: 0,
        failed: changes.length,
        results: [],
        error: message,
      };
    }
  }

  /**
   * ============================================
   * Field Definitions Endpoints (Pipedrive Parity)
   * ============================================
   */

  /**
   * Get cached field definitions from backend
   *
   * @param category Optional category filter (Events, Calls & Emails, Drops)
   * @returns Promise resolving to field definitions response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.getFieldDefinitions();
   * console.log(`Loaded ${Object.keys(result.fields).length} field definitions`);
   * ```
   */
  async getFieldDefinitions(category?: string): Promise<FieldDefinitionsResponse> {
    try {
      logger.log(`[EPG API] Fetching field definitions (category=${category || 'all'})`);

      const response = await apiClient.get<FieldDefinitionsResponse>('epg/field-definitions', {
        params: {
          ...(category ? { category } : {}),
          _t: Date.now(), // Cache buster - force fresh data
        },
      });

      logger.log(
        `[EPG API] Fetched ${Object.keys(response.data.fields).length} field definitions`
      );

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch field definitions:', message);
      throw new Error(message);
    }
  }

  /**
   * Sync field definitions from Pipedrive API
   *
   * Refreshes the cached field definitions by fetching from Pipedrive.
   * Use sparingly as this makes an API call to Pipedrive.
   *
   * @returns Promise resolving to sync response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.syncFieldDefinitions();
   * console.log(`Synced ${result.fields_updated} fields from Pipedrive`);
   * ```
   */
  async syncFieldDefinitions(): Promise<SyncFieldDefinitionsResponse> {
    try {
      logger.log('[EPG API] Syncing field definitions from Pipedrive');

      const response = await apiClient.post<SyncFieldDefinitionsResponse>(
        'epg/field-definitions/sync'
      );

      logger.log(
        `[EPG API] Synced ${response.data.fields_updated} fields from Pipedrive`
      );

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to sync field definitions:', message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Event Completion Endpoints
   * ============================================
   */

  /**
   * Mark an event as completed for an organization
   *
   * @param params Completion parameters
   * @returns Promise resolving to completion response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.markEventCompleted({
   *   orgId: 12345,
   *   eventId: 909,
   *   fieldKey: 'email_invite_sent',
   *   month: '2026-01',
   *   campaignType: 'email_invite',
   * });
   * console.log(`Event marked as completed at ${result.completion.completed_at}`);
   * ```
   */
  async markEventCompleted(params: {
    orgId: number;
    eventId: number;
    fieldKey: string;
    month: string;
    campaignType?: string;
    metadata?: Record<string, unknown>;
  }): Promise<{ success: boolean; completion: EventCompletion }> {
    try {
      logger.log(`[EPG API] Marking event ${params.eventId} as completed for org ${params.orgId}`);

      const response = await apiClient.post<{
        success: boolean;
        completion: EventCompletion;
      }>('/epg/events/mark-completed', {
        org_id: params.orgId,
        event_id: params.eventId,
        field_key: params.fieldKey,
        month: params.month,
        campaign_type: params.campaignType || 'email_invite',
        metadata: params.metadata,
      });

      logger.log(`[EPG API] Event ${params.eventId} marked as completed`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to mark event ${params.eventId} as completed:`, message);
      throw new Error(message);
    }
  }

  /**
   * Bulk mark multiple events as completed
   *
   * @param completions Array of completion data
   * @returns Promise resolving to bulk completion response
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.bulkMarkCompleted([
   *   { orgId: 12345, eventId: 909, fieldKey: 'email_sent', month: '2026-01' },
   *   { orgId: 12346, eventId: 911, fieldKey: 'email_sent', month: '2026-01' },
   * ]);
   * console.log(`Marked ${result.count} events as completed`);
   * ```
   */
  async bulkMarkCompleted(
    completions: Array<{
      orgId: number;
      eventId: number;
      fieldKey: string;
      month: string;
      campaignType?: string;
      metadata?: Record<string, unknown>;
    }>
  ): Promise<{
    success: boolean;
    completions: EventCompletion[];
    count: number;
  }> {
    try {
      logger.log(`[EPG API] Bulk marking ${completions.length} events as completed`);

      const response = await apiClient.post<{
        success: boolean;
        completions: EventCompletion[];
        count: number;
      }>('/epg/events/bulk-mark-completed', {
        completions: completions.map((c) => ({
          org_id: c.orgId,
          event_id: c.eventId,
          field_key: c.fieldKey,
          month: c.month,
          campaign_type: c.campaignType || 'email_invite',
          metadata: c.metadata,
        })),
      });

      logger.log(`[EPG API] Bulk marked ${response.data.count} events as completed`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to bulk mark events as completed:', message);
      throw new Error(message);
    }
  }

  /**
   * Get completion statuses for an organization
   *
   * @param orgId Organization ID
   * @param month Optional month filter (format: "2026-01")
   * @returns Promise resolving to completions list
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.getOrgCompletions(12345, '2026-01');
   * console.log(`Found ${result.count} completions for org 12345`);
   * ```
   */
  async getOrgCompletions(
    orgId: number,
    month?: string
  ): Promise<{
    completions: EventCompletion[];
    count: number;
  }> {
    try {
      logger.log(`[EPG API] Fetching completions for org ${orgId} (month=${month || 'all'})`);

      const params = month ? { month } : {};
      const response = await apiClient.get<{
        completions: EventCompletion[];
        count: number;
      }>(`/epg/organizations/${orgId}/completions`, { params });

      logger.log(`[EPG API] Fetched ${response.data.count} completions for org ${orgId}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to fetch completions for org ${orgId}:`, message);
      throw new Error(message);
    }
  }

  /**
   * ============================================
   * Push Progress Tracking (Smart Polling)
   * ============================================
   */

  /**
   * Get current progress of a push operation
   *
   * Returns the current status and progress of a push to Pipedrive.
   * Used for smart polling to update UI with real-time progress.
   *
   * @param pushId Unique identifier for the push operation
   * @returns Promise resolving to push progress
   *
   * @example
   * ```ts
   * const progress = await epgApi.getPushProgress('push_123456');
   * console.log(`Progress: ${progress.current}/${progress.total}`);
   * ```
   */
  async getPushProgress(pushId: string): Promise<{
    current: number;
    total: number;
    status: string;
    message?: string;
  }> {
    try {
      logger.log(`[EPG API] Getting push progress for ${pushId}`);

      const response = await apiClient.get<{
        current: number;
        total: number;
        status: string;
        message?: string;
        rate?: number;
        estimated_time_remaining?: number;
      }>(`/epg/push-progress/${pushId}`);

      const progress = response.data;

      logger.log(`[EPG API] Push progress for ${pushId}:`, {
        current: progress.current,
        total: progress.total,
        status: progress.status
      });

      return progress;
    } catch (error) {
      // If the backend doesn't have the endpoint yet, fall back to mock
      if (apiClient.isAxiosError && apiClient.isAxiosError(error) && error.response?.status === 404) {
        logger.warn('[EPG API] Push progress endpoint not found, using mock data');

        // Mock implementation for backwards compatibility
        const timestamp = parseInt(pushId.split('_')[1] || '0');
        const elapsed = Date.now() - timestamp;
        const elapsedSeconds = elapsed / 1000;
        const estimatedRate = 20;
        const progress = Math.floor(elapsedSeconds * estimatedRate);
        const total = parseInt(sessionStorage.getItem(`push_${pushId}_total`) || '100');
        const current = Math.min(progress, total);

        let status: string;
        if (current >= total) {
          status = 'completed';
        } else if (elapsedSeconds > 300) {
          status = 'error';
        } else {
          status = 'pushing';
        }

        return {
          current,
          total,
          status,
          message: status === 'completed' ? 'All updates successfully pushed' :
                   status === 'error' ? 'Push timed out' :
                   `Pushing update ${current}/${total}...`
        };
      }

      const message = handleApiError(error);
      logger.error('[EPG API] Failed to get push progress:', message);
      // Return idle state instead of throwing error (non-critical)
      return {
        current: 0,
        total: 0,
        status: 'idle',
        message: 'Progress tracking unavailable'
      };
    }
  }

  /**
   * Start tracking a push operation
   *
   * Initializes backend progress tracking for a new push.
   *
   * @param pushId Unique identifier for the push operation
   * @param total Total number of updates to push
   * @returns Promise resolving when tracking is started
   */
  async startPushProgress(pushId: string, total: number): Promise<void> {
    try {
      logger.log(`[EPG API] Starting push progress tracking for ${pushId} with ${total} updates`);

      await apiClient.post(`/epg/push-progress/${pushId}/start`, null, {
        params: { total }
      });

      logger.log(`[EPG API] Push progress tracking started for ${pushId}`);
    } catch (error) {
      // Non-critical error - progress tracking is optional
      logger.warn('[EPG API] Failed to start push progress tracking:', error);
    }
  }

  /**
   * Clear push progress data
   *
   * Removes progress tracking data from backend after completion.
   *
   * @param pushId Unique identifier for the push operation
   * @returns Promise resolving when data is cleared
   */
  async clearPushProgress(pushId: string): Promise<void> {
    try {
      logger.log(`[EPG API] Clearing push progress for ${pushId}`);

      await apiClient.delete(`/epg/push-progress/${pushId}`);

      logger.log(`[EPG API] Push progress cleared for ${pushId}`);
    } catch (error) {
      // Non-critical error - cleanup is optional
      logger.warn('[EPG API] Failed to clear push progress:', error);
    }
  }

  /**
   * ============================================
   * Contact Role Override Endpoints
   * ============================================
   */

  /**
   * Update a contact's role category override
   *
   * When a user drags a contact to a different role column in the EPG UI,
   * this method saves that override. The override takes precedence over
   * the auto-detected role based on job title.
   *
   * @param contactId Pipedrive person ID
   * @param roleCategory 'CXO', 'Finance', 'HR', or null to remove override
   * @returns Promise resolving when the update is complete
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * // Set a contact to CXO role
   * await epgApi.updateContactRole(12345, 'CXO');
   *
   * // Remove override and revert to auto-detection
   * await epgApi.updateContactRole(12345, null);
   * ```
   */
  async updateContactRole(
    contactId: number,
    roleCategory: 'CXO' | 'Finance' | 'HR' | null
  ): Promise<{
    success: boolean;
    contact_id: number;
    role_category: 'CXO' | 'Finance' | 'HR' | null;
    message: string;
  }> {
    try {
      logger.log(`[EPG API] Updating contact ${contactId} role to ${roleCategory || 'auto-detect'}`);

      const response = await apiClient.put<{
        success: boolean;
        contact_id: number;
        role_category: 'CXO' | 'Finance' | 'HR' | null;
        message: string;
      }>(`/epg/contacts/${contactId}/role`, { role_category: roleCategory });

      logger.log(`[EPG API] Updated contact ${contactId} role: ${response.data.message}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to update contact ${contactId} role:`, message);
      throw new Error(message);
    }
  }

  /**
   * Get all role overrides
   *
   * Returns a dictionary mapping contact_id to role_category for all contacts
   * that have been manually assigned to a role column.
   *
   * Use this to hydrate the EPG UI with existing overrides on page load.
   *
   * @returns Promise resolving to overrides dictionary
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const overrides = await epgApi.getRoleOverrides();
   * console.log(`Found ${Object.keys(overrides).length} role overrides`);
   * // { 12345: 'CXO', 67890: 'Finance' }
   * ```
   */
  async getRoleOverrides(): Promise<Record<number, string>> {
    try {
      logger.log('[EPG API] Fetching role overrides');

      const response = await apiClient.get<{
        success: boolean;
        count: number;
        overrides: Record<number, string>;
      }>('/epg/contacts/role-overrides');

      logger.log(`[EPG API] Fetched ${response.data.count} role overrides`);

      return response.data.overrides;
    } catch (error) {
      const message = handleApiError(error);
      logger.error('[EPG API] Failed to fetch role overrides:', message);
      throw new Error(message);
    }
  }

  /**
   * Delete a contact's role override
   *
   * Removes the manual role override for a contact, reverting to auto-detection.
   * This is equivalent to calling updateContactRole with null.
   *
   * @param contactId Pipedrive person ID
   * @returns Promise resolving when the delete is complete
   *
   * @throws Error if API request fails
   *
   * @example
   * ```ts
   * const result = await epgApi.deleteContactRole(12345);
   * if (result.deleted) {
   *   console.log('Override removed');
   * }
   * ```
   */
  async deleteContactRole(contactId: number): Promise<{
    success: boolean;
    contact_id: number;
    deleted: boolean;
    message: string;
  }> {
    try {
      logger.log(`[EPG API] Deleting role override for contact ${contactId}`);

      const response = await apiClient.delete<{
        success: boolean;
        contact_id: number;
        deleted: boolean;
        message: string;
      }>(`/epg/contacts/${contactId}/role`);

      logger.log(`[EPG API] Delete role override result: ${response.data.message}`);

      return response.data;
    } catch (error) {
      const message = handleApiError(error);
      logger.error(`[EPG API] Failed to delete contact ${contactId} role override:`, message);
      throw new Error(message);
    }
  }
}

/**
 * Global EPG API client instance
 */
export const epgApi = new EPGApiClient();

/**
 * Export as default for convenience
 */
export default epgApi;

